import { HazardScenario } from '../types/hazard.types'
import { isMeaningfulParamValue } from './hazard.constants'

export type ScenarioGridParameters = {
  rowKey: string
  colKey: string
}

const DEFAULT_GRID_PARAMETERS: ScenarioGridParameters = {
  rowKey: 'slr',
  colKey: 'year',
}

const isNumericKey = (key: string, uniqueCounts: Map<string, Set<string | number>>): boolean => {
  const first = [...(uniqueCounts.get(key) ?? [])][0]
  return typeof first === 'number'
}

const avgLabelLength = (key: string, uniqueCounts: Map<string, Set<string | number>>): number => {
  const values = [...(uniqueCounts.get(key) ?? [])]
  if (values.length === 0) return 0
  return values.reduce((sum: number, v) => sum + String(v).length, 0) / values.length
}

const pickByLabelLength = (
  candidates: [string, Set<string | number>][],
  uniqueCounts: Map<string, Set<string | number>>,
): string | undefined => {
  return candidates
    .slice()
    .sort((a, b) => avgLabelLength(a[0], uniqueCounts) - avgLabelLength(b[0], uniqueCounts))[0]?.[0]
}

/**
 * Resolves which scenario parameters map to grid rows and columns.
 *
 * Strategy:
 * - Counts unique meaningful values per parameter key (numeric or string)
 * - Only considers parameters with more than one unique value (actual variance)
 * - Prefers numeric parameters for BOTH axes — string parameters only win
 *   when no numeric candidate is available
 * - Among same-type candidates, picks shortest average label length for
 *   the column axis — avoids wide columns with long string values
 * - Falls back to DEFAULT_GRID_PARAMETERS (slr × year) if no variance found
 */
export const resolveScenarioGridParameters = (
  scenarios: HazardScenario[],
): ScenarioGridParameters => {
  if (scenarios.length === 0) return DEFAULT_GRID_PARAMETERS

  const uniqueCounts = new Map<string, Set<string | number>>()

  for (const scenario of scenarios) {
    for (const [key, val] of Object.entries(scenario.parameters)) {
      if (!isMeaningfulParamValue(val)) continue
      if (typeof val !== 'number' && typeof val !== 'string') continue
      if (!uniqueCounts.has(key)) uniqueCounts.set(key, new Set())
      uniqueCounts.get(key)!.add(val)
    }
  }

  // only keep parameters with more than one unique meaningful value
  const candidates = [...uniqueCounts.entries()]
    .filter(([, values]) => values.size > 1)
    .sort((a, b) => b[1].size - a[1].size)

  if (candidates.length === 0) return DEFAULT_GRID_PARAMETERS

  if (candidates.length === 1) {
    const [rowKey] = candidates[0]
    const singleColKey =
      [...uniqueCounts.keys()].filter(
        (key) => key !== rowKey && isNumericKey(key, uniqueCounts),
      )[0] ?? DEFAULT_GRID_PARAMETERS.colKey
    return { rowKey, colKey: singleColKey }
  }

  const numericCandidates = candidates.filter(([key]) => isNumericKey(key, uniqueCounts))
  const stringCandidates = candidates.filter(([key]) => !isNumericKey(key, uniqueCounts))

  // row: highest-variance numeric, or fall back to highest-variance string
  const [rowKey] = numericCandidates[0] ?? stringCandidates[0] ?? candidates[0]

  const remaining = candidates.filter(([key]) => key !== rowKey)
  const numericRemaining = remaining.filter(([key]) => isNumericKey(key, uniqueCounts))
  const stringRemaining = remaining.filter(([key]) => !isNumericKey(key, uniqueCounts))

  // col: prefer numeric with shortest label length, fall back to string
  const colCandidates = numericRemaining.length > 0 ? numericRemaining : stringRemaining
  const colKey = pickByLabelLength(colCandidates, uniqueCounts) ?? DEFAULT_GRID_PARAMETERS.colKey

  return { rowKey, colKey }
}
